import { useCallback, useEffect, useState } from 'react';
import { formatCents } from '@lbhl/shared';
import {
  deleteBid,
  fetchAllBids,
  restoreBid,
  type AdminBidRow,
} from '../lib/api.js';
import { describeServerRemoval } from '../lib/removal.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { OverflowMenu } from './OverflowMenu.js';

/**
 * Every bid on the server, and where an admin deletes them.
 *
 * Separate from the estimator's **Saved** tab, which lists what is on *that
 * device*. Deleting here removes the server's copy; a phone that already synced
 * still shows its own local row until that device is next used, and re-syncing it
 * will be refused rather than silently restoring the bid.
 */
export function AllBids() {
  const [bids, setBids] = useState<AdminBidRow[] | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  /** The bid awaiting confirmation, if any. One dialog serves the whole list. */
  const [pending, setPending] = useState<AdminBidRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setBids(await fetchAllBids(showDeleted));
    } catch (err) {
      setError((err as Error).message);
    }
  }, [showDeleted]);

  useEffect(() => void reload(), [reload]);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await fn());
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      setPending(null);
    }
  }

  const live = (bids ?? []).filter((b) => b.deleted_at === null);
  const deleted = (bids ?? []).filter((b) => b.deleted_at !== null);

  return (
    <>
      {error && <div className="note bad">{error}</div>}
      {notice && <div className="note">{notice}</div>}

      <section className="card">
        <h2>
          All bids ({live.length})
          <button
            className="ghost"
            style={{ float: 'right', marginTop: -6 }}
            onClick={() => setShowDeleted((v) => !v)}
          >
            {showDeleted ? 'Hide deleted' : 'Show deleted'}
          </button>
        </h2>

        <div className="note">
          This is the server's record, across everyone. Deleting a bid nobody has been
          sent removes it for good; a bid that reached a customer is hidden and can be
          restored, because somebody was quoted that number.
        </div>

        {bids === null ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>Loading…</div>
        ) : live.length === 0 ? (
          <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>No bids yet.</div>
        ) : (
          live.map((b) => (
            <BidRow key={b.id} bid={b} busy={busy} onAskDelete={() => setPending(b)} />
          ))
        )}
      </section>

      {pending && (
        <ConfirmDialog
          copy={describeServerRemoval({
            status: pending.status,
            customerName: pending.customer_name,
            totalCents: pending.total_cents,
          })}
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={() =>
            void run(async () => {
              const outcome = await deleteBid(pending.id);
              return outcome.action === 'deleted'
                ? `Bid for ${pending.customer_name ?? 'unnamed customer'} (${formatCents(pending.total_cents)}) deleted.`
                : outcome.reason;
            })
          }
        />
      )}

      {showDeleted && (
        <section className="card">
          <h2>Deleted, kept on record ({deleted.length})</h2>
          {deleted.length === 0 ? (
            <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>
              Nothing here. Bids that never reached a customer are removed outright rather
              than kept.
            </div>
          ) : (
            deleted.map((b) => (
              <div className="bidrow" key={b.id}>
                <span className="who">
                  <b>{b.customer_name ?? 'Unnamed customer'}</b>
                  <span>
                    {b.address_line1 ?? 'no address'} · {b.created_by_email} · deleted{' '}
                    {new Date(b.deleted_at!).toLocaleDateString()}
                  </span>
                  <span className="badge warn">Was {b.status}</span>
                </span>
                <span className="amt">{formatCents(b.total_cents)}</span>
                <button
                  className="ghost"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await restoreBid(b.id);
                      return 'Bid restored.';
                    })
                  }
                >
                  Restore
                </button>
              </div>
            ))
          )}
        </section>
      )}
    </>
  );
}

function BidRow({
  bid,
  busy,
  onAskDelete,
}: {
  bid: AdminBidRow;
  busy: boolean;
  onAskDelete(): void;
}) {
  return (
    <div className="bidrow">
      <span className="who">
        <b>{bid.customer_name ?? 'Unnamed customer'}</b>
        <span>
          {bid.address_line1 ?? 'no address'} · {bid.season_label} · {bid.created_by_email} ·{' '}
          {new Date(bid.created_at).toLocaleDateString()}
        </span>
        {bid.selected_package !== null && (
          <span className="badge">Chose {bid.selected_package}</span>
        )}
        {bid.status !== 'final' && bid.status !== 'draft' && (
          <span className="badge">{bid.status}</span>
        )}
        {bid.adjustment_total_cents !== 0 && (
          <span className="badge warn">
            Adjusted {formatCents(bid.adjustment_total_cents)}
          </span>
        )}
        {bid.needs_review && <span className="badge bad">Flagged for review</span>}
        {bid.quoted_on_superseded && <span className="badge warn">Quoted on old pricing</span>}
      </span>

      {/* All three quoted prices. The margin figure is the full-scope tier's, which
          is what the denormalised column holds. */}
      <span className="amt">
        <span className="amts">
          <span>{formatCents(bid.essential_total_cents)}</span>
          <span>{formatCents(bid.signature_total_cents)}</span>
          <span>{formatCents(bid.showcase_total_cents)}</span>
        </span>
        {bid.margin_pct !== null && (
          <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-dim)' }}>
            {Number(bid.margin_pct)}% margin
          </span>
        )}
      </span>

      <OverflowMenu
        label={`Actions for the bid for ${bid.customer_name ?? 'an unnamed customer'}`}
        items={[
          {
            label: ['sent', 'accepted', 'declined'].includes(bid.status)
              ? 'Hide bid…'
              : 'Delete bid…',
            danger: true,
            disabled: busy,
            onSelect: onAskDelete,
          },
        ]}
      />
    </div>
  );
}
